import { DateTime } from 'luxon';

/**
 * Patterns matching valid partial inputs during typing
 * [dd, dd-mm, dd-mm-(yy)yy?]
 * @type {readonly RegExp[]}
 */
const PARTIAL_DATE_PATTERNS = [
  /^(0[1-9]|[12]\d|3[01])$/,
  /^(0[1-9]|[12]\d|3[01])-(0[1-9]|1[0-2])$/,
  /^(0[1-9]|[12]\d|3[01])-(0[1-9]|1[0-2])-((?:19|20)?\d{2})$/,
];

/**
 * Handles the application's lightweight dd-mm-yyyy input mask.
 *
 * It deliberately does not rewrite the whole value, which means the browser
 * can retain normal caret behaviour while editing.
 * @param {string} value
 * @returns {string}
 */
export function formatDateInput(value) {
  if (PARTIAL_DATE_PATTERNS[0].test(value)) {
    return `${value}-`;
  }

  if (PARTIAL_DATE_PATTERNS[1].test(value)) {
    return `${value}-`;
  }

  return value;
}

/**
 * Checks if input string satisfies the complete date regex mask.
 * @param {string} value
 * @returns {boolean}
 */
export function isCompleteDateInput(value) {
  return PARTIAL_DATE_PATTERNS[2].test(value);
}

/**
 * Turns dd-mm-yy into dd-mm-yyyy using the same century rule as the
 * original application. Four-digit years are returned unchanged.
 * @param {string} year
 * @returns {string}
 */
function normalizeYear(year) {
  if (year.length === 4) {
    return year;
  }

  const currentYear = DateTime.now().year;
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const currentYearWithinCentury = currentYear % 100;
  const numericYear = Number(year);

  return numericYear > currentYearWithinCentury
    ? String(currentCentury - 100 + numericYear)
    : String(currentCentury + numericYear);
}

/**
 * Returns the ISO date represented by a Danish dd-mm-(yy)yy value,
 * or null when the value is incomplete/invalid.
 * @param {string} value
 * @returns {DateTime<true> | null} // DateTime<true> | null
 */
export function parseDateInput(value) {
  const parts = value.split('-');

  if (parts.length !== 3 || !PARTIAL_DATE_PATTERNS[2].test(value)) {
    return null;
  }

  const [day, month, rawYear] = parts;
  const year = normalizeYear(rawYear);
  const date = DateTime.fromObject({
    day: Number(day),
    month: Number(month),
    year: Number(year),
  });

  return date.isValid ? date : null;
}

/**
 * Evaluates whether input contains a fully parsed Luxon Date object.
 * @param {string} value 
 * @returns {boolean}
 */
export function isValidDateInput(value) {
  return parseDateInput(value) !== null;
}

/**
 * Skifter rød baggrundsfarve på dato-feltet alt efter om inputtet er validt eller ej
 * @param {HTMLInputElement | null} input Element to mark as valid or invalid.
 * @param {boolean} invalid 
 * @returns {void}
 */
export function markDateInvalid(input, invalid) {
  if (!input) return;

  input.classList.toggle(
    'has-background-danger-light',
    invalid,
  );

  if (invalid) {
    input.setAttribute('aria-invalid', 'true');
  } else {
    input.removeAttribute('aria-invalid');
  }
}